import { VariantColor } from '@repo/toolkit/colors'
import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import TextInformation from '@repo/ui/text-information'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'

import { getTripFolder } from '../../../api/trip-folder/get-trip-folder-details'
import { Paths } from '../../../lib/constants/path'
import { TripProperties } from '../../../lib/types/models/trip/properties.trip'
import { TripAddress } from '../../../lib/types/models/trip-address/trip-address'
import { TripFolder } from '../../../lib/types/models/trip-folder/trip-folder'
import { TripFolderProperties } from '../../../lib/types/models/trip-folder/trip-folder-properties'
import { Time } from '../../../lib/types/utils'
import { filterDuplicates } from '../../../lib/utils/functions/filter-duplicates'
import { TripFolderTimezone } from '../../../views/trip-folder/trip-folder-details/folder-details'
import CountryFlag from '../../elements/country-flag'
import TimeBadge from '../../elements/time-badge'

const COLOR_VARIANTS: VariantColor[] = ['warning', 'success', 'blue', 'purple', 'violet']

interface Props {
  folderId: number
  activeTimezone: TripFolderTimezone
}

interface TimeBadgesProps {
  times: Time[] | string[]
  useColorVariants?: boolean
}

interface PlacesProps {
  places: TripAddress[]
}

const TimeBadges = ({ times, useColorVariants = false }: TimeBadgesProps) => {
  return (
    <div className="flex flex-wrap gap-2">
      {times?.length
        ? times.map((time, index) => (
            <TimeBadge
              key={index}
              time={time}
              variant={useColorVariants ? COLOR_VARIANTS[index % COLOR_VARIANTS.length] : 'grey'}
            />
          ))
        : '-'}
    </div>
  )
}

const Places = ({ places }: PlacesProps) => {
  const { navigate } = useNavigator()

  return (
    <div className="flex flex-col items-start gap-1">
      {places?.length
        ? places.map((place, index) => (
            <div className="flex gap-2" key={index}>
              <CountryFlag possibleCountry={place?.country} />
              <Typography.Link
                variant="secondary-body"
                onClick={() =>
                  navigate(Paths.TRIP_ADDRESS_UPDATE_PAGE.replace(':id', place.id.toString()))
                }
              >
                {place?.label}
              </Typography.Link>
            </div>
          ))
        : '-'}
    </div>
  )
}

export const formatTripFolderDetails = (data: TripFolder) => {
  const tripBookings = TripFolderProperties.getTripBookingsDetails(data)

  return { ...data, tripBookings }
}

const TripFolderDetailsSection = ({ folderId, activeTimezone }: Props) => {
  const { data: tripFolder } = useQuery({
    queryKey: ['trip-folder', folderId],
    queryFn: () => getTripFolder(folderId),
    select: formatTripFolderDetails,
    enabled: !!folderId
  })

  const handleConvertTimeZone = (time: Time | string | undefined, timezone?: string) => {
    if (!timezone || !time) return DateFormatter.getFormattedTime(time)
    return DateFormatter.convertTimeZone(
      time,
      timezone,
      activeTimezone === 'Local' ? timezone : activeTimezone
    )
  }

  return (
    <div className="grid grid-cols-4 gap-4">
      <TextInformation
        title="Date de départ"
        component={
          <Badge
            text={valueRender(tripFolder?.departureDate, (date) =>
              DateFormatter.DDDMYYYY(new Date(date))
            )}
            variant="warning"
          />
        }
      />

      <TextInformation
        title="Date d'arrivée"
        component={
          <Badge
            text={valueRender(tripFolder?.arrivalDate, (date) =>
              DateFormatter.DDDMYYYY(new Date(date))
            )}
            variant="warning"
          />
        }
      />

      <TextInformation
        title="Heure(s) de RDV Parents"
        component={
          <TimeBadges
            times={
              tripFolder?.tripBookings
                .map((data) =>
                  handleConvertTimeZone(
                    data.meetingTimeForParents,
                    data.meetingTimeForParentsTimeZone
                  )
                )
                .filter((time) => !!time) ?? []
            }
            useColorVariants={true}
          />
        }
      />

      <TextInformation
        title="Voyage"
        component={
          <div className="flex flex-col gap-2">
            {filterDuplicates(tripFolder?.tripBookings ?? [], (data) => data.trip.id).map(
              (data) => (
                <Typography variant="secondary-body" key={data.trip.id}>
                  {TripProperties.getTripLabel(data.trip)}
                </Typography>
              )
            )}
          </div>
        }
      />

      <TextInformation
        title="Heure(s) de départ"
        component={
          <TimeBadges
            times={
              tripFolder?.tripBookings
                .map((data) => handleConvertTimeZone(data.departureTime, data.departureTimeZone))
                .filter((time) => !!time) ?? []
            }
            useColorVariants={true}
          />
        }
      />

      <TextInformation
        title="Lieu(x) de départ"
        component={
          <Places
            places={
              filterDuplicates(tripFolder?.tripBookings ?? [], (data) => data.departurePlace?.id)
                .map((data) => data.departurePlace)
                .filter((place): place is TripAddress => !!place) ?? []
            }
          />
        }
      />

      <TextInformation
        title="Heure(s) d'arrivée"
        component={
          <TimeBadges
            times={
              tripFolder?.tripBookings
                .map((data) => handleConvertTimeZone(data.arrivalTime, data.arrivalTimeZone))
                .filter((time) => !!time) ?? []
            }
            useColorVariants={true}
          />
        }
      />

      <TextInformation
        title="Lieu(x) d'arrivée"
        component={
          <Places
            places={
              filterDuplicates(tripFolder?.tripBookings ?? [], (data) => data.arrivalPlace?.id)
                .map((data) => data.arrivalPlace)
                .filter((place): place is TripAddress => !!place) ?? []
            }
          />
        }
      />
    </div>
  )
}

export default TripFolderDetailsSection
